import React, { useState, useCallback, useEffect, useRef } from 'react';
import './ImageSlider.css'; // Import the external CSS file

// SVG Icon Components (kept for potential future use)
const ChevronLeftIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
    </svg>
);

const ChevronRightIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
);

const useWindowWidth = () => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowWidth;
};

const ImageSlider = ({
    images = [],
    autoPlay = true,
    autoPlayInterval = 3000,
    showNavButtons = false,
    onViewAllClick
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const autoPlayRef = useRef(null);
    const dragThreshold = 50;
    const width = useWindowWidth();

    // Use the images prop directly. Ensure it's always an array.
    const sliderImages = Array.isArray(images) ? images : [];

    useEffect(() => {
        if (sliderImages.length > 0) {
            setActiveIndex(Math.floor(sliderImages.length / 2));
        }
    }, [sliderImages.length]);

    const nextSlide = useCallback(() => {
        if (sliderImages.length === 0) return;
        setActiveIndex((prevIndex) => (prevIndex + 1) % sliderImages.length);
    }, [sliderImages.length]);

    const prevSlide = useCallback(() => {
        if (sliderImages.length === 0) return;
        setActiveIndex((prevIndex) => (prevIndex - 1 + sliderImages.length) % sliderImages.length);
    }, [sliderImages.length]);

    const startAutoPlay = useCallback(() => {
        if (!autoPlay || sliderImages.length === 0) return;
        stopAutoPlay();
        autoPlayRef.current = setInterval(nextSlide, autoPlayInterval);
    }, [nextSlide, autoPlay, autoPlayInterval, sliderImages.length]);

    const stopAutoPlay = () => {
        if (autoPlayRef.current) {
            clearInterval(autoPlayRef.current);
        }
    };

    useEffect(() => {
        startAutoPlay();
        return () => stopAutoPlay();
    }, [startAutoPlay, activeIndex]);

    const handleDragStart = (e) => {
        stopAutoPlay();
        setIsDragging(true);
        const currentX = e.touches ? e.touches[0].clientX : e.clientX;
        setStartX(currentX);
    };

    const handleDragMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
    };

    const handleDragEnd = (e) => {
        if (!isDragging) return;
        setIsDragging(false);
        const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
        const diff = startX - endX;

        if (diff > dragThreshold) {
            nextSlide();
        } else if (diff < -dragThreshold) {
            prevSlide();
        }
        startAutoPlay();
    };



    const getSlideStyle = (index) => {
        const total = sliderImages.length;
        let offset = index - activeIndex;

        if (offset > total / 2) offset -= total;
        else if (offset < -total / 2) offset += total;

        const isVisible = Math.abs(offset) <= 1;
        const translationPercentage = width <= 575 ? (28000 / width) : width <= 638 ? (30000 / width) : width <= 768 ? (34000 / width) : 35;

        const transform = `translateX(${offset * translationPercentage}%) scale(${offset === 0 ? 1.1 : 0.8})`;
        const opacity = isVisible ? (offset === 0 ? 1 : 0.6) : 0;
        const zIndex = total - Math.abs(offset);
        const pointerEvents = isVisible ? 'auto' : 'none';

        return { transform, opacity, zIndex, pointerEvents };
    };

    const handleViewAllClick = () => {
        if (onViewAllClick) {
            onViewAllClick();
        }
    };

    return (
        <div className="slider-app-container">
            <div className="slider-app">
                <div
                    className="slider-container"
                    onMouseDown={handleDragStart}
                    onMouseMove={handleDragMove}
                    onMouseUp={handleDragEnd}
                    onMouseLeave={() => isDragging && handleDragEnd({})}
                    onTouchStart={handleDragStart}
                    onTouchMove={handleDragMove}
                    onTouchEnd={handleDragEnd}
                >
                    {showNavButtons && (
                        <button onClick={prevSlide} className="nav-button prev-button" aria-label="Previous slide">
                            <ChevronLeftIcon />
                        </button>
                    )}

                    <div className="slides-wrapper">
                        {sliderImages.map((image, index) => {
                            // **FIX:** Check if the 'image' is a string (URL) or an object
                            const imageUrl = typeof image === 'string' ? image : (image.imageUrl || image.src || image.url);
                            const imageKey = typeof image === 'string' ? index : (image.id || index);
                            const imageAlt = typeof image === 'string'
                                ? `Slide ${index + 1}`
                                : (image.title || image.alt || `Slide ${index + 1}`);

                            return (
                                <div
                                    key={imageKey}
                                    className={index === activeIndex ? 'slide active' : 'slide'}
                                    style={getSlideStyle(index)}
                                >
                                    <img
                                        src={imageUrl}
                                        alt={imageAlt}
                                        className="slide-image"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://placehold.co/600x400/4a1d1f/FFFFFF?text=Image';
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {showNavButtons && (
                        <button onClick={nextSlide} className="nav-button next-button" aria-label="Next slide">
                            <ChevronRightIcon />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImageSlider;
